import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme
import { FaClock } from "react-icons/fa";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { useAuth } from "../../hooks/AuthenticateContext";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import DeleteIcon from "../../assets/images/deleteIcon.svg";
import EditIcon from "../../assets/images/editIcon.svg";
import InfoIcon from "../../assets/images/infoIcon.svg";
import { useBreadcrumb } from "../../hooks/BreadCrumbContext";
import { usePermissaoTela } from "../../hooks/usePermissaoTela";
import api from "../../services/api";
import Loading from "./components/Loading";
import Status from "./components/Status";
import { SkeletonCard, SkeletonTable } from "../SkeletonLoading";
import { CustomButtons, GridProps, GridHandle } from "./model/Grid";
import { Tooltip } from "@mui/material";
import CustomFilter from "./components/CustomFilter";
import CustomStatusFilter from "./components/CustomStatusFilter";

import "./styles.css";

const Grid = forwardRef((props: GridProps, ref: React.Ref<GridHandle>) => {
  const gridRef = useRef<any>(null);
  const { user } = useAuth();
  // Ações da linha respeitam as permissões da tela (SALVAR = editar,
  // REMOVER = excluir, CONHECER = visualizar).
  const { podeSalvar, podeRemover, podeConhecer } = usePermissaoTela(props.permissionKey);
  const [columns] = useState(props.columns);
  const [colDefs, setColDefs] = useState<any>([]);
  const [pagination] = useState(props.pagination);
  const [rowSelection] = useState(props.rowSelection);
  const [path] = useState(props.path);
  const [rowData, setRowData] = useState<any[]>();
  const [pageSize, setPageSize] = useState(20);
  const [isMobile, setIsMobile] = useState(false);
  const { addBreadcrumb } = useBreadcrumb();
  const [loading, setLoading] = useState(false);
  const [mobileHasMore, setMobileHasMore] = useState(false);
  const [mobileLoadingMore, setMobileLoadingMore] = useState(false);
  const isRestoring = useRef(false);
  const customFiltersRef = useRef(props.customFilters);
  const isMobileRef = useRef(false);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const mobilePageRef = useRef(1);
  const mobileFetchInProgressRef = useRef(false);
  const mobilePageSize = 20;

  // Chave de persistência do estado das colunas (ordem, fixação, visibilidade).
  // Quando a tela informa `persistFiltersKey` mantemos o sufixo antigo para não
  // invalidar o que já está salvo; caso contrário, derivamos uma chave estável
  // a partir da rota + endpoint da grid para que a reordenação feita pelo
  // usuário sobreviva à navegação entre telas.
  const columnStateStorageKey = props.persistFiltersKey
    ? `${props.persistFiltersKey}_columnState`
    : `gridColumnState:${typeof window !== "undefined" ? window.location.pathname : ""}:${props.path}${props.isModal ? ":modal" : ""}`;

  const buildRequestFilters = useCallback((filterModel?: any) => {
    let filters: any = {};

    if (customFiltersRef.current) {
      filters = { ...filters, ...customFiltersRef.current };
    }

    if (filterModel != null) {
      for (const customFilter in filterModel) {
        const newFilter: any = filterModel[customFilter];
        const filterField = newFilter.field || customFilter;
        if (filterField === "data_historico") {
          if (newFilter.value && newFilter.value.length > 0) {
            filters["data_inicial"] = newFilter.value[0];
            filters["data_final"] = newFilter.value[1];
          }
        } else if (filterField.includes("data") && Array.isArray(newFilter.value)) {
          if (newFilter.value.length > 0) {
            filters[`${filterField}1`] = newFilter.value[0];
            filters[`${filterField}2`] = newFilter.value[1];
          }
        } else {
          const valueFilter =
            filterField === "status"
              ? Number(newFilter.value)
              : newFilter.value;
          filters[`${filterField}`] = newFilter.filter || valueFilter;
        }
      }
    }

    if (user?.id_terminal && filters.id_terminal === undefined) {
      filters.id_terminal = user.id_terminal;
    }
    if (user?.id_transportadora && filters.id_transportadora === undefined) {
      filters.id_transportadora = user.id_transportadora;
    }

    return filters;
  }, [user]);

  const fetchMobileRows = useCallback(async (page = 1, append = false) => {
    if (mobileFetchInProgressRef.current) return;

    try {
      mobileFetchInProgressRef.current = true;
      if (append) {
        setMobileLoadingMore(true);
      } else {
        setLoading(true);
        setMobileHasMore(false);
      }

      const gridApi = gridRef.current;
      const filterModel = gridApi?.getFilterModel?.() || {};
      const sortModel = gridApi?.getSortModel?.() || [];
      const filters = buildRequestFilters(filterModel);

      let orderBy = props.defaultSortField || "data_historico";
      if (sortModel.length > 0) {
        const sortColId = sortModel[0].colId;
        const colDef = columns.find((col) => col.field === sortColId);
        orderBy = (colDef?.fieldName || sortColId).replace("uf_estado", "id_estado");
      }

      const response = await api.post(`${path}?page=${page}`, {
        qtd_por_pagina: mobilePageSize,
        order_by: orderBy,
        order_direction: sortModel.length > 0 ? sortModel[0].sort : "desc",
        ...filters,
      });

      const mappingResponse = (response.data?.data || []).map((item: any) => ({
        ...item,
        status: item.status ?? 0,
      }));
      const total = Number(response.data?.total || 0);

      setRowData((currentRows = []) => (
        append ? [...currentRows, ...mappingResponse] : mappingResponse
      ));
      mobilePageRef.current = page;
      setMobileHasMore((page * mobilePageSize) < total && mappingResponse.length > 0);
    } catch {
      if (!append) {
        setRowData([]);
      }
      setMobileHasMore(false);
    } finally {
      setLoading(false);
      setMobileLoadingMore(false);
      mobileFetchInProgressRef.current = false;
    }
  }, [buildRequestFilters, columns, path, props.defaultSortField]);

  const handleClearFilters = useCallback(() => {
    if (props.persistFiltersKey) {
      localStorage.removeItem(props.persistFiltersKey);
    }
    localStorage.removeItem(columnStateStorageKey);

    const apiInstance = gridRef.current;
    if (apiInstance) {
      if (typeof apiInstance.setFilterModel === "function") {
        apiInstance.setFilterModel(null);
      }
      if (typeof apiInstance.onFilterChanged === "function") {
        apiInstance.onFilterChanged();
      }
    }

    if (isMobileRef.current) {
      void fetchMobileRows(1, false);
    } else if (apiInstance) {
      if (typeof apiInstance.purgeInfiniteCache === "function") {
        apiInstance.purgeInfiniteCache();
      } else if (typeof apiInstance.refreshInfiniteCache === "function") {
        apiInstance.refreshInfiniteCache();
      }
    }
  }, [fetchMobileRows, props.persistFiltersKey, columnStateStorageKey]);

  useEffect(() => {
    const updateMobileLayout = () => {
      const mobile = window.innerWidth < 768;
      isMobileRef.current = mobile;
      setIsMobile(mobile);
    };

    updateMobileLayout();
    window.addEventListener("resize", updateMobileLayout);

    return () => window.removeEventListener("resize", updateMobileLayout);
  }, []);

  useImperativeHandle(ref, () => ({
    api: gridRef.current,
    refresh: () => {
      if (isMobile) {
        void fetchMobileRows(1, false);
        return;
      }

      const api = gridRef.current;
      if (!api) return;

      if (typeof api.purgeInfiniteCache === "function") {
        api.purgeInfiniteCache();
        return;
      }

      if (typeof api.refreshInfiniteCache === "function") {
        api.refreshInfiniteCache();
      }
    },
    clearFilters: handleClearFilters,
    getFilters: () => {
      let filters: any = {};

      if (customFiltersRef.current) {
        filters = { ...filters, ...customFiltersRef.current };
      }

      const api = gridRef.current;
      if (!api) return filters;

      const filterModel = api.getFilterModel();

      if (filterModel) {
        for (const customFilter in filterModel) {
          let newFilter: any = filterModel[customFilter];
          if (newFilter.field === "data_historico") {
            if (newFilter.value.length > 0) {
              filters["data_inicial"] = newFilter.value[0];
              filters["data_final"] = newFilter.value[1];
            }
          } else if (newFilter.field && newFilter.field.includes("data") && Array.isArray(newFilter.value)) {
            if (newFilter.value.length > 0) {
              filters[`${newFilter.field}1`] = newFilter.value[0];
              filters[`${newFilter.field}2`] = newFilter.value[1];
            }
          } else {
            let field = newFilter.field || customFilter;
            const valueFilter =
              field === "status"
                ? Number(newFilter.value)
                : newFilter.value;
            filters[`${field}`] = newFilter.filter || valueFilter;
          }
        }
      }

      if (user?.id_terminal && filters.id_terminal === undefined) {
        filters.id_terminal = user.id_terminal;
      }
      if (user?.id_transportadora && filters.id_transportadora === undefined) {
        filters.id_transportadora = user.id_transportadora;
      }

      return filters;
    }
  }), [fetchMobileRows, handleClearFilters, isMobile, user]);

  useEffect(() => {
    customFiltersRef.current = props.customFilters;
    if (isMobileRef.current) {
      void fetchMobileRows(1, false);
      return;
    }

    if (gridRef.current) {
      if (typeof gridRef.current.refreshInfiniteCache === 'function') {
        gridRef.current.refreshInfiniteCache();
      } else if (gridRef.current.api && typeof gridRef.current.api.refreshInfiniteCache === 'function') {
        gridRef.current.api.refreshInfiniteCache();
      }
    }
  }, [fetchMobileRows, props.customFilters]);

  useEffect(() => {
    if (isMobile) {
      void fetchMobileRows(1, false);
    }
  }, [fetchMobileRows, isMobile]);

  const shouldShowCustomButton = (button: CustomButtons, data: any) => {
    let showButtonStatus = !button.status;
    if (data.id_transportadora) {
      showButtonStatus = true;
    }
    if (button.status && button.status.length > 0) {
      const findButtonStatus = button.status.find(
        (item) => item == data.status
      );
      if (findButtonStatus != undefined) {
        showButtonStatus = true;
      }
    }

    if (showButtonStatus && button.shouldShow) {
      return button.shouldShow(data);
    }

    return showButtonStatus;
  };

  const renderRowActions = (data: any, isCard = false) => {
    if (!data) return null;

    return (
      <div className={`flex items-center ${isCard ? "gap-2" : "justify-center w-full h-full"}`}>
        {props.onView && podeConhecer && (
          <button
            className={isCard ? "sulog-mobile-card-action" : "mr-4"}
            onClick={() => {
              addBreadcrumb("Conhecer");
              props.onView!(data);
            }}
            id="btnInfo"
            type="button"
            aria-label="Conhecer"
          >
            <img src={InfoIcon} style={{ width: 16, height: 16 }} />
          </button>
        )}
        {props.customButtons &&
          props.customButtons.map((button: CustomButtons) => {
            if (!shouldShowCustomButton(button, data)) return null;

            return (
              <button
                key={button.label}
                className={isCard ? "sulog-mobile-card-action" : "flex cursor-pointer items-center justify-center h-full mr-4"}
                onClick={() => button.action(data)}
                id={"btnDelete"}
                type="button"
                aria-label={button.label}
              >
                <Tooltip title={button.label}>
                  <img
                    className="w-[22px] h-[22px]"
                    src={button.icon(data)}
                    alt=""
                  />
                </Tooltip>
              </button>
            );
          })}
        {!window.location.pathname.includes("triagens") &&
          props.onUpdate && podeSalvar && (
            <button
              className={isCard ? "sulog-mobile-card-action" : "mr-4"}
              onClick={() => {
                addBreadcrumb("Editar");
                props.onUpdate!(data);
              }}
              id="btnEdit"
              type="button"
              aria-label="Editar"
            >
              <img src={EditIcon} style={{ width: 16, height: 16 }} />
            </button>
          )}
        {data.status <= 10 && props.onDelete && podeRemover && (
          <button
            className={isCard ? "sulog-mobile-card-action" : ""}
            onClick={() => props.onDelete!(data)}
            id="btnDelete"
            type="button"
            aria-label="Excluir"
          >
            <img src={DeleteIcon} style={{ width: 16, height: 16 }} />
          </button>
        )}
        {props.onHistory && (user?.grupo === "ADMINISTRADOR" || user?.grupo === "SUPERVISOR") && (
          <Tooltip title="Histórico">
            <button
              className={isCard ? "sulog-mobile-card-action" : "ml-4"}
              onClick={() => props.onHistory!(data)}
              id="btnHistory"
              type="button"
              aria-label="Histórico"
            >
              <FaClock style={{ width: 16, height: 16, color: '#6B7280' }} />
            </button>
          </Tooltip>
        )}
      </div>
    );
  };

  const getNestedValue = (data: any, path?: string) => {
    if (!data || !path) return undefined;

    return path.split(".").reduce((currentValue, pathPart) => {
      if (currentValue === undefined || currentValue === null) return undefined;
      return currentValue[pathPart];
    }, data);
  };

  const getMobileColumnValue = (column: any, data: any) => {
    if (!column) return "---";

    const rawValue = column.valueGetter
      ? column.valueGetter({ data })
      : getNestedValue(data, column.field);

    const formattedValue = column.valueFormatter
      ? (column.valueFormatter as (params: any) => any)({
        value: rawValue,
        data,
      })
      : rawValue;

    if (formattedValue === undefined || formattedValue === null || formattedValue === "") {
      return "---";
    }

    return formattedValue;
  };

  const mobileColumns = useMemo(() => {
    return columns.filter((column: any) => column.headerName && column.field);
  }, [columns]);

  const mobileTitleColumn = useMemo(() => {
    return mobileColumns.find((column: any) =>
      String(column.headerName).toLowerCase().includes("placa dianteira")
    ) || mobileColumns[0];
  }, [mobileColumns]);

  const mobileSubtitleColumn = useMemo(() => {
    return mobileColumns.find((column: any) =>
      column.field !== mobileTitleColumn?.field &&
      String(column.headerName).toLowerCase().includes("data de entrada")
    ) || mobileColumns.find((column: any) => column.field !== mobileTitleColumn?.field);
  }, [mobileColumns, mobileTitleColumn?.field]);

  const defaultColumns = [
    // {
    //   field: "",
    //   headerName: "",
    //   width: 50,
    //   pinned: "left",
    //   checkboxSelection: true,
    //   checkboxHeaderSelection: true,
    // },
    {
      field: "",
      headerName: "",
      headerComponent: () => (
        <div className="flex items-center justify-center w-full h-full">
          <button
            type="button"
            onClick={handleClearFilters}
            className="flex items-center justify-center h-8 px-3 bg-[#062D4E] text-[#FFFFFF] text-xs font-light border-none rounded-full gap-1.5 hover:bg-[#0a3d6a] transition-colors cursor-pointer shadow-sm"
            id="btnClearFilters"
            title="Limpar todos os filtros aplicados"
          >
            <div className="w-4 h-4 rounded-full bg-[#E53E3E] flex items-center justify-center text-white text-[10px] font-bold leading-none flex-shrink-0">
              ✕
            </div>
            <span className="whitespace-nowrap">Limpar Filtros</span>
          </button>
        </div>
      ),
      pinned: "right",
      sortable: false,
      filter: false,
      cellRenderer: (params: CustomCellRendererProps) => {
        if (!params.data) return null;
        return renderRowActions(params.data);
      },
    },
  ];

  const loadingOverlayComponent = useMemo(() => Loading, []);

  const onFilterChanged = useCallback((params: any) => {
    console.log("entrou", params);
    const gridApi = params.api;

    if (props.persistFiltersKey) {
      const filterModel = gridApi.getFilterModel();
      localStorage.setItem(props.persistFiltersKey, JSON.stringify(filterModel));
    }

    if (isRestoring.current) return;

    const filter = gridApi.getFilterModel()["operacao_porto_agendada.tipo_carga"];

    console.log(filter);
  }, [props.persistFiltersKey]);

  const handleMobileScroll = useCallback(() => {
    const listElement = mobileListRef.current;
    if (!listElement || !mobileHasMore || loading || mobileLoadingMore) return;

    const distanceToBottom = listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight;
    if (distanceToBottom <= 96) {
      void fetchMobileRows(mobilePageRef.current + 1, true);
    }
  }, [fetchMobileRows, loading, mobileHasMore, mobileLoadingMore]);

  const onColumnStateChanged = useCallback((params: any) => {
    if (isRestoring.current) return;
    // `onColumnMoved` dispara várias vezes durante o arraste; só persistimos ao
    // soltar (`finished`). Eventos de ordenação/fixação/visibilidade não trazem
    // o campo, então seguem normalmente.
    if (params?.finished === false) return;
    const columnApi = params.columnApi || params.api;
    if (!columnApi?.getColumnState) return;
    localStorage.setItem(columnStateStorageKey, JSON.stringify(columnApi.getColumnState()));
  }, [columnStateStorageKey]);

  const onGridReady = useCallback(
    (params: any) => {
      gridRef.current = params.api;

      // Monta colunas
      let cols: any[] = [];
      defaultColumns.forEach((defaultColumn: any) => {
        cols.unshift(defaultColumn);
      });

      if (props.isShowStatus) {
        cols.unshift({
          field: "status",
          fieldName: "status",
          headerName: "Status",
          filter: CustomStatusFilter,
          filterParams: { status: props.status },
          cellStyle: { textAlign: "center" },
          cellRenderer: (params: CustomCellRendererProps) => {
            if (params.data) {
              return (
                <Status
                  data={props.status}
                  status={params.data.status}
                  statusData={props.getStatusData?.(params.data, props.status)}
                />
              );
            }
          },
        });
      }

      columns.forEach((column) => {
        if (column.headerName) {
          column.headerName = column.headerName;
        }
        if (column.filter === true) {
          column.filter = CustomFilter;
        }
        cols.push(column);
      });

      setColDefs(cols);
      params.api.updateGridOptions({ columnDefs: cols });

      isRestoring.current = true;

      if (props.persistFiltersKey) {
        const savedFilters = localStorage.getItem(props.persistFiltersKey);

        if (savedFilters) {
          try {
            const parsedFilters = JSON.parse(savedFilters);
            console.log("Restoring filters:", parsedFilters);
            params.api.setFilterModel(parsedFilters);
          } catch (e) {
            console.error("Error restoring filters", e);
          }
        }
      }

      // O estado das colunas (ordem, fixação, visibilidade) é restaurado em toda
      // grid, com ou sem `persistFiltersKey`, para que a reordenação feita pelo
      // usuário não se perca ao trocar de tela e voltar.
      const savedColumnState = localStorage.getItem(columnStateStorageKey);
      if (savedColumnState) {
        try {
          const parsedColumnState = JSON.parse(savedColumnState);
          console.log("Restoring column state:", parsedColumnState);
          // Delay column state restoration to ensure it overrides any filter-based moves
          setTimeout(() => {
            const restoreApi = params.columnApi || params.api;
            restoreApi.applyColumnState({ state: parsedColumnState, applyOrder: true });
          }, 100);
        } catch (e) {
          console.error("Error restoring column state", e);
        }
      }

      setTimeout(() => {
        isRestoring.current = false;
        params.api.setDatasource(dataSource);
      }, 500);
      const dataSource = {
        getRows: async (gridParams: any) => {
          try {
            setLoading(true);
            console.log("getRows params:", gridParams);
            const pageSize = gridParams.endRow - gridParams.startRow;
            console.log(pageSize);
            const page = Math.floor(gridParams.startRow / pageSize) + 1;
            let filters: any = {};

            console.log(params);

            if (customFiltersRef.current) {
              filters = { ...filters, ...customFiltersRef.current }
            }

            if (gridParams.filterModel != null) {
              for (const customFilter in gridParams.filterModel) {
                let newFilter: any = gridParams.filterModel[customFilter];
                const filterField = newFilter.field || customFilter;
                if (filterField === "data_historico") {
                  if (newFilter.value && newFilter.value.length > 0) {
                    filters["data_inicial"] = newFilter.value[0];
                    filters["data_final"] = newFilter.value[1];
                  }
                } else if (filterField.includes("data") && Array.isArray(newFilter.value)) {
                  if (newFilter.value.length > 0) {
                    filters[`${filterField}1`] = newFilter.value[0];
                    filters[`${filterField}2`] = newFilter.value[1];
                  }
                } else {
                  const valueFilter =
                    filterField === "status"
                      ? Number(newFilter.value)
                      : newFilter.value;
                  filters[`${filterField}`] = newFilter.filter || valueFilter;
                }
              }
            }

            if (user?.id_terminal && filters.id_terminal === undefined) {
              filters.id_terminal = user.id_terminal;
            }
            if (user?.id_transportadora && filters.id_transportadora === undefined) {
              filters.id_transportadora = user.id_transportadora;
            }

            console.log(gridParams.sortModel);

            // Get the field name for sorting
            let orderBy = props.defaultSortField || "data_historico";
            if (gridParams.sortModel.length > 0) {
              const sortColId = gridParams.sortModel[0].colId;
              // Find the column definition to get fieldName
              const colDef = cols.find(col => col.field === sortColId);
              orderBy = colDef?.fieldName || sortColId;
              // Keep the existing replacement for uf_estado -> id_estado
              orderBy = orderBy.replace("uf_estado", "id_estado");
            }

            const reqDTO = {
              qtd_por_pagina: pageSize,
              order_by: orderBy,
              order_direction:
                gridParams.sortModel.length > 0
                  ? gridParams.sortModel[0].sort
                  : "desc",
              ...filters,
            };

            const response = await api.post(
              `${path}?page=${page}`,
              reqDTO
            );

            const mappingResponse = response.data.data.map((item: any) => {
              return {
                ...item,
                status: item.status ?? 0
              }
            })

            gridParams.successCallback(
              mappingResponse,
              response.data.total
            );
            if (!isMobileRef.current) {
              setRowData(mappingResponse);
            }
            setLoading(false);
          } catch {
            setLoading(false);
          }
        },
      };

      params.api.addEventListener("paginationChanged", () => {
        const newSize = params.api.paginationGetPageSize();
        if (newSize !== pageSize) {
          setPageSize(newSize);
          // força getRows com novo tamanho
        }
      });

    },
    [pageSize]
  );

  return (
    <div
      className="sulog-grid ag-theme-quartz relative w-full min-w-0 bg-[#FFFFFF] rounded-lg p-3 shadow-md"
      style={{ width: "100%", height: props.isModal ? "calc(70vh - 120px)" : "calc(100vh - 160px)" }}
    >
      {loading && !isMobile && (
        <div className="absolute top-[52px] bottom-0 left-0 right-0 z-20 bg-white px-3 pb-3 rounded-b-lg overflow-hidden pointer-events-none">
          <SkeletonTable showHeader={false} rows={7} columns={columns?.length || 6} />
        </div>
      )}

      {isMobile && (
        <div
          ref={mobileListRef}
          className="sulog-mobile-card-list"
          onScroll={handleMobileScroll}
        >
          {rowData && rowData.length > 0 ? (
            <>
              <span className="sulog-mobile-card-count">
                Mostrando {rowData.length} registros
              </span>
              {rowData.map((row, rowIndex) => {
                const rowClassName = props.getRowClass?.(row) || "";

                return (
                <article
                  key={row.id || row.id_operacao_patio || rowIndex}
                  className={`sulog-mobile-card ${rowClassName}`.trim()}
                >
                  <div className="sulog-mobile-card-header">
                    <div className="min-w-0">
                      <span className="sulog-mobile-card-title">
                        {getMobileColumnValue(mobileTitleColumn, row)}
                      </span>
                      {mobileSubtitleColumn && (
                        <span className="sulog-mobile-card-subtitle">
                          {getMobileColumnValue(mobileSubtitleColumn, row)}
                        </span>
                      )}
                    </div>
                    {renderRowActions(row, true)}
                  </div>

                  {props.isShowStatus && (
                    <div className="sulog-mobile-card-row">
                      <span>Status</span>
                      <Status
                        data={props.status}
                        status={row.status}
                        statusData={props.getStatusData?.(row, props.status)}
                      />
                    </div>
                  )}

                  <div className="sulog-mobile-card-fields">
                    {mobileColumns.map((column: any) => (
                      <div key={column.field} className="sulog-mobile-card-row">
                        <span>{column.headerName}</span>
                        <strong>{getMobileColumnValue(column, row)}</strong>
                      </div>
                    ))}
                  </div>
                </article>
                );
              })}
              {mobileLoadingMore && (
                <div className="sulog-mobile-card-loading">
                  Carregando mais registros...
                </div>
              )}
              {!mobileHasMore && rowData.length >= mobilePageSize && (
                <div className="sulog-mobile-card-loading">
                  Todos os registros foram carregados.
                </div>
              )}
            </>
          ) : (
            <div className="sulog-mobile-card-empty w-full">
              {loading ? <SkeletonCard count={3} /> : "Nenhum registro encontrado."}
            </div>
          )}
        </div>
      )}

      <div className="sulog-grid-table">
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          rowModelType="infinite"
          pagination={pagination}
          paginationPageSize={pageSize}
          cacheBlockSize={pageSize}
          rowSelection={rowSelection}
          loadingOverlayComponent={loadingOverlayComponent}
          loadingCellRenderer={loadingOverlayComponent}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          onColumnMoved={onColumnStateChanged}
          onColumnPinned={onColumnStateChanged}
          onColumnVisible={onColumnStateChanged}
          enableCellTextSelection={true}
          getRowClass={(params) => props.getRowClass?.(params.data) || ""}

          onSortChanged={(params: any) => {
            const sortModel = params.api.getSortModel();
            console.log("Current sort model:", sortModel);

            // Log the adjusted sort model with fieldName
            const adjustedSortModel = sortModel.map((sm: any) => {
              const colDef = colDefs.find((col: any) => col.field === sm.colId);
              return {
                ...sm,
                colId: colDef?.field || sm.colId,
              };
            });

            console.log("Sort model with fieldName:", adjustedSortModel);
            onColumnStateChanged(params);
          }}

        />
      </div>
    </div>
  );
});

Grid.displayName = "Grid";

export default Grid;
